/**
 * krv/gemeinsam.js – geteilte Bausteine der KRV-Seiten (Welle 95).
 *
 * Styles, Zahlen-/Datumsformate und der API-Helfer liegen hier, damit die
 * Belegliste und die Sektion der Leistungsabrechnungen dasselbe benutzen,
 * ohne sich gegenseitig zu importieren.
 */
import React from "react";
import { API_BASE } from "../../api/config";

export const API = `${API_BASE}/krv/api`;

export const BEREICH_FARBE = {
  nicht_zugeordnet: ["var(--color-gray-200)", "var(--color-gray-800)"],
  einzureichen: ["var(--color-warning-bg)", "var(--color-warning)"],
  eingereicht: ["var(--color-info-bg)", "var(--color-info)"],
  bezahlt: ["var(--color-success-bg)", "var(--color-success)"],
};

export const S = {
  page: { maxWidth: 1400, margin: "0 auto", padding: "24px 20px 60px", color: "var(--color-gray-800)" },
  h1: { fontSize: "1.5rem", margin: "0 0 4px" },
  sub: { color: "var(--color-gray-600)", margin: "0 0 18px", fontSize: "0.9rem" },
  karte: { background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10, marginBottom: 14, overflow: "hidden" },
  kopf: { display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer", userSelect: "none" },
  kopfTitel: { fontSize: "1.05rem", fontWeight: 700, margin: 0, flex: "0 0 auto" },
  zaehler: { fontSize: "0.85rem", color: "var(--color-gray-600)" },
  buendel: { padding: "6px 16px 6px 34px", background: "var(--color-gray-100)", borderTop: "1px solid var(--color-gray-200)", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 },
  tabelle: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: { textAlign: "left", padding: "6px 10px", borderBottom: "1px solid var(--color-gray-300)", color: "var(--color-gray-600)", fontSize: "0.78rem", whiteSpace: "nowrap" },
  td: { padding: "6px 10px", borderBottom: "1px solid var(--color-gray-200)", verticalAlign: "top" },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  btn: { padding: "6px 12px", border: "1px solid var(--color-gray-300)", borderRadius: 6, background: "var(--color-gray-100)", color: "var(--color-gray-800)", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 },
  btnPrim: { padding: "6px 12px", border: "none", borderRadius: 6, background: "var(--color-info)", color: "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 },
  input: { padding: "5px 8px", border: "1px solid var(--color-gray-300)", borderRadius: 6, fontSize: "0.82rem", background: "var(--bg-card)", color: "var(--color-gray-800)", width: "100%" },
  err: { background: "var(--color-error-bg)", border: "1px solid var(--color-error)", borderRadius: 6, padding: "8px 12px", fontSize: "0.85rem", color: "var(--color-error)", marginBottom: 10 },
  info: { background: "var(--color-info-bg)", border: "1px solid var(--color-info)", borderRadius: 6, padding: "8px 12px", fontSize: "0.85rem", color: "var(--color-info)", marginBottom: 10 },
  badge: (b) => ({
    display: "inline-block", padding: "2px 9px", borderRadius: 12, fontSize: "0.75rem", fontWeight: 700,
    background: (BEREICH_FARBE[b] || BEREICH_FARBE.nicht_zugeordnet)[0],
    color: (BEREICH_FARBE[b] || BEREICH_FARBE.nicht_zugeordnet)[1],
  }),
  gesperrt: { opacity: 0.55, pointerEvents: "none" },
  btnRot: { padding: "6px 12px", border: "none", borderRadius: 6, background: "var(--color-error)", color: "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 },
  btnIcon: { padding: 0, margin: "0 0 0 8px", border: "none", background: "none", cursor: "pointer", fontSize: "0.95rem", lineHeight: 1 },
  btnMini: { padding: "1px 6px", margin: "0 0 0 6px", border: "1px solid var(--color-gray-300)", borderRadius: 5, background: "var(--bg-card)", color: "var(--color-gray-700)", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  dialog: { background: "var(--bg-card)", borderRadius: 10, padding: 20, width: 460, maxWidth: "92vw", textAlign: "left", cursor: "default" },
  // Runde Icon-Knöpfe im Stil der Verschieben-Seite. Wichtig: padding/margin müssen
  // explizit auf 0 stehen — die App-weite Regel `button { padding: 10px 20px }` und
  // `button + button { margin-left: 8px }` quetschen sonst jedes schmale Icon platt.
  iconBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 26, padding: 0, margin: 0, marginLeft: 4, flexShrink: 0,
    borderRadius: 4, cursor: "pointer", fontSize: "0.8rem", lineHeight: 1,
    border: "1px solid rgba(100,140,255,0.35)", background: "rgba(100,140,255,0.10)",
    color: "#8eaaff", transition: "all 0.15s",
  },
  iconFarbe: {
    blau:   { border: "1px solid rgba(100,140,255,0.35)", background: "rgba(100,140,255,0.10)", color: "#8eaaff" },
    orange: { border: "1px solid rgba(255,167,38,0.35)",  background: "rgba(255,167,38,0.10)",  color: "#ffa726" },
    rot:    { border: "1px solid rgba(239,83,80,0.35)",   background: "rgba(239,83,80,0.10)",   color: "#ef5350" },
    gruen:  { border: "1px solid rgba(102,187,106,0.35)", background: "rgba(102,187,106,0.10)", color: "#66bb6a" },
    grau:   { border: "1px solid rgba(144,164,174,0.35)", background: "rgba(144,164,174,0.10)", color: "#90a4ae" },
  },
  iconZelle: { whiteSpace: "nowrap", textAlign: "center" },
  // Welle 96: bei offener Vorschau wird es eng — Icons brechen dann in zwei Reihen um
  iconGruppe: { display: "flex", flexWrap: "wrap", gap: "4px 0", justifyContent: "flex-end", minWidth: 64, maxWidth: 164 },
  filterKnopf: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 20, height: 18, padding: 0, margin: "0 0 0 5px", border: "none",
    borderRadius: 3, background: "transparent", color: "var(--color-gray-500)",
    cursor: "pointer", fontSize: "0.7rem", lineHeight: 1, flexShrink: 0,
  },
  filterAktiv: { background: "var(--color-info-bg)", color: "var(--color-info)" },
  filterBox: {
    position: "absolute", zIndex: 900, marginTop: 4, minWidth: 230, maxWidth: 340,
    background: "var(--bg-card)", border: "1px solid var(--color-gray-300)",
    borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.35)", padding: 10,
    textAlign: "left", fontWeight: 400,
  },
  filterListe: { maxHeight: 220, overflowY: "auto", margin: "8px 0", fontSize: "0.8rem" },
  filterZeile: { display: "flex", gap: 6, alignItems: "center", padding: "2px 0", cursor: "pointer" },
  zeileMahnung: { background: "var(--color-gray-100)" },
  pillMahnung: { display: "inline-block", padding: "1px 7px", marginRight: 6, borderRadius: 10, fontSize: "0.7rem", fontWeight: 700, background: "var(--color-warning-bg)", color: "var(--color-warning)" },
  // Welle 96: Vorschau rechts, gewählte Zeile eingerahmt
  layout: { display: "flex", gap: 16, alignItems: "flex-start" },
  layoutLinks: { flex: "1 1 0", minWidth: 0 },
  layoutRechts: { flex: "0 0 42%", position: "sticky", top: 10, alignSelf: "flex-start" },
  vorschau: { background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10,
              overflow: "hidden", height: "calc(100vh - 20px)", display: "flex", flexDirection: "column" },
  vorschauKopf: { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                  borderBottom: "1px solid var(--color-gray-200)" },
  vorschauRahmen: { flex: 1, width: "100%", border: "none", background: "#525659" },
  zeileMarkiert: { background: "var(--color-info-bg)" },
  zeileGewaehlt: { outline: "2px solid var(--color-info)", outlineOffset: -2, background: "var(--color-info-bg)" },
  aufklapper: { display: "inline-block", width: 16, cursor: "pointer", color: "var(--color-gray-600)", userSelect: "none" },
  sortKnopf: { cursor: "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 3 },
  pillTeilweise: { display: "inline-block", padding: "1px 7px", marginLeft: 6, borderRadius: 10, fontSize: "0.7rem", fontWeight: 700, background: "var(--color-warning-bg)", color: "var(--color-warning)", whiteSpace: "nowrap" },
  suchLeiste: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "10px 14px", marginBottom: 14, background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10 },
  pillWarn: { display: "inline-block", padding: "1px 7px", marginLeft: 6, borderRadius: 10, fontSize: "0.7rem", fontWeight: 700, background: "var(--color-error-bg)", color: "var(--color-error)" },
};

/** Anzeigename je Bereich — auch für die, die nicht im Belegablauf stehen. */
export const BEREICH_NAME = {
  nicht_zugeordnet: "Nicht zugeordnet",
  einzureichen: "Einzureichen",
  eingereicht: "Eingereicht",
  bezahlt: "Bezahlt",
  abrechnung: "Leistungsabrechnungen",
  mahnung: "Mahnungen",
  sonstiges: "Sonstiges",
  steuer: "Steuer",
  kostenvoranschlag: "Kostenvoranschlag",
};

/** Welle 96: Diese Ziele verschiebt das Status-Auswahlfeld sofort, ohne Datumsdialog. */
export const BEREICHE_DIREKT = ["mahnung", "sonstiges", "steuer", "kostenvoranschlag"];

export const eur = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €");
// Welle 104: immer zweistellig — 07.07.2026 statt 7.7.2026
export const datum = (v) => (v ? new Date(v).toLocaleDateString("de-DE",
  { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

/** Welle 104: Erstattungs-Kennzeichen eines Belegs in Bezahlt (vorne im Dateinamen). */
export const ERSTATTET_NAME = { voll: "voll erstattet", teilweise: "teilweise erstattet", keine: "nicht erstattet" };
export const ERSTATTET_ZEICHEN = { teilweise: "<", keine: ">" };
export const heute = () => new Date().toISOString().slice(0, 10);

export async function api(pfad, method = "GET", body) {
  const init = { method, headers: {} };
  if (body !== undefined) { init.headers["Content-Type"] = "application/json"; init.body = JSON.stringify(body); }
  const r = await fetch(`${API}${pfad}`, init);
  const ct = r.headers.get("content-type") || "";
  const d = ct.includes("json") ? await r.json() : await r.text();
  if (!r.ok) throw new Error((d && d.detail) || r.statusText);
  return d;
}


/* ── Icon-Knopf ─────────────────────────────────────────────────────────── */
export function IconKnopf({ farbe = "blau", titel, onClick, disabled, children }) {
  const [hover, setHover] = React.useState(false);
  const stil = {
    ...S.iconBtn, ...(S.iconFarbe[farbe] || S.iconFarbe.blau),
    ...(hover && !disabled ? { transform: "scale(1.08)", filter: "brightness(1.35)" } : null),
    ...(disabled ? { opacity: 0.35, cursor: "not-allowed" } : null),
  };
  return (
    <button type="button" style={stil} title={titel} disabled={disabled}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            onClick={(e) => { e.stopPropagation(); onClick && onClick(e); }}>
      <span style={{ display: "flex", flexShrink: 0 }}>{children}</span>
    </button>
  );
}

/* ── Spaltenfilter wie in Excel ─────────────────────────────────────────—— */
/**
 * Klick auf den Trichter öffnet eine Liste aller in dieser Spalte vorkommenden
 * Werte mit Häkchen und Suchfeld. Ohne Auswahl ist die Spalte ungefiltert.
 */
export function FilterKopf({ label, werte, auswahl, onAendern, rechts = false, sortierung, onSortieren }) {
  const [offen, setOffen] = React.useState(false);
  const [suche, setSuche] = React.useState("");
  const aktiv = auswahl && auswahl.size > 0;
  const sichtbar = werte.filter((w) => w.toLowerCase().includes(suche.toLowerCase()));

  const umschalten = (w) => {
    const n = new Set(auswahl || []);
    if (n.has(w)) n.delete(w); else n.add(w);
    onAendern(n);
  };

  return (
    <th style={{ ...S.th, textAlign: rechts ? "right" : "left", position: "relative" }}>
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {onSortieren ? (
          <span style={S.sortKnopf} title="sortieren (aufsteigend → absteigend → aus)"
                onClick={(e) => { e.stopPropagation(); onSortieren(); }}>
            {label}
            <span style={{ fontSize: "0.65rem", width: 8, color: sortierung ? "var(--color-info)" : "var(--color-gray-400)" }}>
              {sortierung === "asc" ? "▲" : sortierung === "desc" ? "▼" : "⇅"}
            </span>
          </span>
        ) : label}
        <button type="button" title={aktiv ? `${auswahl.size} ausgewählt` : "filtern"}
                style={{ ...S.filterKnopf, ...(aktiv ? S.filterAktiv : null) }}
                onClick={(e) => { e.stopPropagation(); setOffen((o) => !o); }}>
          {aktiv ? "▼" : "▽"}
        </button>
      </span>
      {offen && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 890 }} onClick={() => setOffen(false)} />
          <div style={{ ...S.filterBox, [rechts ? "right" : "left"]: 0 }} onClick={(e) => e.stopPropagation()}>
            <input autoFocus style={S.input} placeholder="Suchen…" value={suche}
                   onChange={(e) => setSuche(e.target.value)} />
            <div style={S.filterListe}>
              {sichtbar.length === 0 && (
                <div style={{ color: "var(--color-gray-500)" }}>Nichts gefunden</div>
              )}
              {sichtbar.map((w) => (
                <label key={w} style={S.filterZeile}>
                  <input type="checkbox" checked={!!auswahl && auswahl.has(w)}
                         onChange={() => umschalten(w)} />
                  <span style={{ wordBreak: "break-all" }}>{w || "(leer)"}</span>
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "space-between" }}>
              <button style={S.btnMini} onClick={() => onAendern(new Set(sichtbar))}>alle</button>
              <button style={S.btnMini} onClick={() => { onAendern(new Set()); setSuche(""); }}>
                zurücksetzen
              </button>
              <button style={S.btnMini} onClick={() => setOffen(false)}>fertig</button>
            </div>
          </div>
        </>
      )}
    </th>
  );
}
